import { Box, Button, Chip } from "@mui/material";
import axios from "axios";
import { MRT_ColumnDef } from "material-react-table";
import { FedReference, FundingPackage, FundingPackageLockMode, FundingPackageStatus, FundingPriority } from "../types";
import FedReferenceLine from "../FedReferenceLine";
import EditIcon from "@mui/icons-material/edit";
import { Link } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { differenceInHours, differenceInMinutes, format } from "date-fns";

export const PriorityLabels: Record<FundingPriority, string> = {
  [FundingPriority.PayoffCutoff]: "Payoff Cutoff",
  [FundingPriority.Now]: "Now",
  [FundingPriority.Soon]: "Soon",
  [FundingPriority.Today]: "Today"
};
export const FundingPackageStatusLabel: Record<FundingPackageStatus, { label: string; color: string }> = {
  [FundingPackageStatus.Draft]: {
    label: "Draft",
    color: "text.primary"
  },
  [FundingPackageStatus.Requested]: {
    label: "Requested",
    color: "info.main"
  },
  [FundingPackageStatus.Deficient]: {
    label: "Deficient",
    color: "error.main"
  },
  [FundingPackageStatus.Funded]: {
    label: "Funded",
    color: "success.main"
  },
  [FundingPackageStatus.Cancelled]: {
    label: "Cancelled",
    color: "text.secondary"
  }
};
export const PriorityStyles: Record<
  FundingPriority,
  {
    color: string;
    border: string;
    bg: string;
  }
> = {
  [FundingPriority.PayoffCutoff]: {
    color: "#d32f2f",
    border: "#d32f2f",
    bg: alpha("#d32f2f", 0.1)
  },
  [FundingPriority.Now]: {
    color: "#ed6c02",
    border: "#ed6c02",
    bg: alpha("#ed6c02", 0.12)
  },
  [FundingPriority.Today]: {
    color: "#1976d2",
    border: "#1976d2",
    bg: alpha("#1976d2", 0.12)
  },
  [FundingPriority.Soon]: {
    color: "#2e7d32",
    border: "#2e7d32",
    bg: alpha("#2e7d32", 0.12)
  }
};

export const getColumns = (fedReferences: Record<number, FedReference[]>): MRT_ColumnDef<FundingPackage>[] => {
  return [
    {
      header: "Order Number",
      accessorKey: "orderNumber",
      id: "orderNumber",
      size: 50,
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      accessorKey: "createdByEmail",
      header: "Created By",
      enableHiding: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Submitted",
      accessorKey: "requestedDate",
      id: "submitted",
      size: 80,
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      },
      Cell: ({ cell }) => {
        const value = cell.getValue() as string | Date | null;
        if (!value) return "-";
        const date = new Date(value);
        const now = new Date();
        const hours = differenceInHours(now, date);
        const minutes = differenceInMinutes(now, date) % 60;
        const timeAgo = hours === 0 ? `${minutes} min ago` : `${hours} hr${hours > 1 ? "s" : ""} ago`;
        const formattedDate = format(date, "MMM d, yyyy HH:mm");
        return `${formattedDate} (${timeAgo})`;
      }
    },
    {
      header: "Priority",
      accessorKey: "priority",
      id: "priority",
      size: 50,
      filterSelectOptions: [
        { label: "Payoff Cutoff", value: "1" },
        { label: "Now", value: "2" },
        { label: "Soon", value: "3" },
        { label: "Today", value: "4" }
      ],
      filterVariant: "select",
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        select: true
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<FundingPriority>();
        const style = PriorityStyles[value];
        return (
          <Chip
            label={PriorityLabels[value]}
            size="small"
            variant="outlined"
            sx={{
              color: style?.color,
              borderColor: style?.border,
              backgroundColor: style?.bg,
              fontWeight: 500
            }}
          />
        );
      }
    },
    {
      header: "Status",
      filterVariant: "multi-select",
      accessorKey: "status",
      filterSelectOptions: [
        { label: "Draft", value: "1" },
        { label: "Requested", value: "2" },
        { label: "Deficient", value: "3" },
        { label: "Funded", value: "4" },
        { label: "Cancelled", value: "5" }
      ],
      id: "status",
      filterFn: (row, columnId, filterValue: string[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(String(row.getValue(columnId)));
      },
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        SelectProps: {
          renderValue: (selected: any) => {
            const vals = selected as string[];
            const labelMap: Record<string, string> = {
              "1": "Draft",
              "2": "Requested",
              "3": "Deficient",
              "4": "Funded",
              "5": "Cancelled"
            };
            if (vals.length <= 2) return vals.map((v: string) => labelMap[v] || v).join(", ");
            return `${vals.length} selected`;
          }
        }
      },
      Cell: ({ cell, row }) => {
        const value = cell.getValue<FundingPackageStatus>();
        const status = FundingPackageStatusLabel[value];

        return (
          <Box>
            <Box sx={{ color: status?.color, fontWeight: value === FundingPackageStatus.Deficient ? 600 : 400 }}>
              {status.label}
            </Box>
            {value === FundingPackageStatus.Funded && <FedReferenceLine fedReferences={fedReferences[row.original.id]} />}
          </Box>
        );
      }
    },
    {
      header: "Claimed By",
      id: "activeLock",
      enableColumnActions: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const activeLock = row.original.activeLock;
        if (!activeLock) return "-";
        return (
          <Chip
            size="small"
            label={`${activeLock.lockedByName || activeLock.lockedByEmail} ${
              activeLock.mode === FundingPackageLockMode.Review ? "reviewing" : "editing"
            }`}
            color={activeLock.mode === FundingPackageLockMode.Review ? "info" : "warning"}
            variant="outlined"
          />
        );
      }
    },
    {
      header: "Actions",
      id: "actions",
      enableColumnActions: false,
      enableSorting: false,
      enableHiding: false,
      Cell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon fontSize="small" />}
          component={Link}
          to={`/edit-funding-package/${row.original.orderNumber}-pkg${row.original.packageNumber}`}
        >
          Edit
        </Button>
      )
    }
  ];
};
